import moment from 'moment';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import REGEX from '../../../config/regex';
import {
  errorCodes,
  getErrorMessageKey,
  getVynePaymentErrorMessageKey,
} from '../../../util/errorCodes';
import formatPrice from '../../../util/formatPrice';
import jsonPost from '../../../util/jsonPost';
import { labelsPropTypes, summaryLoadPropTypes } from './PropTypes';

import { resetAuthCookie } from '../../../util/cookies';
import {
  findCurrencyItemFromListWithFallback,
  getPricePerPaxTypePerCurrency,
  getInitialCurrencyState,
} from '../../../util/currency';
import formatDateAndTime from '../../../util/formatDateAndTime';
import Button from '../Atoms/Button';
import ClientEditDetails from '../BookingFieldset/ClientEditDetails';
import EmailCustomerDetails from '../BookingFieldset/EmailCustomerDetails';
import Insurance from '../BookingFieldset/Insurance';
import LinkedBookings from '../BookingFieldset/LinkedBookings';
import MemberAddress from '../BookingFieldset/MemberAddress';
import MemberDetails from '../BookingFieldset/MemberDetails';
import {
  accomTypes,
  getOption,
  getPassengers,
  initFieldsetProps,
  markRequiredFields,
  updateField,
  validateFieldset,
} from '../BookingFieldset/helpers';
import {
  StopoverTypes,
  getStopoverDataWithDataKey,
  getStopoverGroupWithSelectedStopover,
  getStopoverOptionWithSelectedStopover,
  getTourDatesWithStopoversConsidered,
  isStopoverOfType,
  segregateStopoversByType,
} from '../BookingStopoverOptions/helpers';
import { buildStopoverItineraryDescriptions } from '../BookingUpgrades/helpers';
import { withErrorBoundary } from '../Core/ErrorBoundary';
import ErrorMessage from '../Core/ErrorMessage';
import PackageSummary from '../PackageSummary';
import Picture from '../Picture';
import StickyComponent from '../StickyComponent';
import Discount from './Discount';
import ItineraryInline from './ItineraryInline';
import PaymentOptions from './PaymentOptions';
import TermsConditions from './TermsConditions';
import CUSTOM_EVENT from '../../../util/customEvents';

const propTypes = exact({
  apiURL: PropTypes.string.isRequired,
  cookieDomain: PropTypes.string.isRequired,
  data: summaryLoadPropTypes.isRequired,
  labels: labelsPropTypes,
  orderId: PropTypes.string.isRequired,
});

const defaultProps = {
  labels: undefined,
};

const requiredFields = ['emailAddress', 'fullName.forename', 'fullName.prefix', 'fullName.surname'];
class BookingSummary extends Component {
  constructor(props) {
    super(props);

    this.handleDiscountChange = this.handleDiscountChange.bind(this);
    this.handleEmailCustomerDetailsChange = this.handleEmailCustomerDetailsChange.bind(this);
    this.handleEmailSubmit = this.handleEmailSubmit.bind(this);
    this.handlePaymentOptionsChange = this.handlePaymentOptionsChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleTermsConditionsChange = this.handleTermsConditionsChange.bind(this);
    this.handleResetState = this.handleResetState.bind(this);
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this);

    this.submitContainerRef = React.createRef();

    const { data } = props;

    let leadPartyMember = data.leadBooker;

    leadPartyMember = initFieldsetProps(leadPartyMember);
    markRequiredFields(leadPartyMember, requiredFields);

    const currentCurrency = getInitialCurrencyState(props.data.pricePerCurrency);
    const currencyPriceSetting = findCurrencyItemFromListWithFallback(
      props.data.pricePerCurrency,
      currentCurrency.code
    );

    this.state = {
      currentCurrencyCodeAndSymbol: currentCurrency,
      currencyPriceSetting,
      discountCode: '',
      errorMessage: null,
      isLoading: false,
      marketingSignUp1: props.data.marketingSignUp1,
      marketingSignUp2: props.data.marketingSignUp2,
      marketingSignUp3: props.data.marketingSignUp3,
      marketingSignUp4: props.data.marketingSignUp4,
      paymentOption: '0',
      priorityAccessPass: '',
      sendToCustomer: {
        confirmEmail: {
          value: '',
        },
        email: leadPartyMember.emailAddress,
        firstName: leadPartyMember.fullName.forename,
        surname: leadPartyMember.fullName.surname,
        title: leadPartyMember.fullName.prefix,
      },
      termsConditionsAccepted: false,
      totalPrice: currencyPriceSetting.totalPrice,
      depositPrice: currencyPriceSetting.depositPrice,
      maskMotorhomesAsCamping: props.data.maskMotorhomesAsCamping,
    };
  }

  componentDidMount() {
    window.addEventListener('pageshow', this.handleResetState);
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);

    const urlSearchParams = new URLSearchParams(window.location.search);
    const codeQueryValue = urlSearchParams.get('Code') || urlSearchParams.get('code');

    if (codeQueryValue) {
      const errorMessageKey = getVynePaymentErrorMessageKey(Number(codeQueryValue));

      this.setState(
        {
          errorMessage: this.props.labels[errorMessageKey],
        },
        () => {
          if (this.submitContainerRef.current && this.state.errorMessage) {
            window.scrollTo({ top: this.submitContainerRef.current.getBoundingClientRect().top });
          }
        }
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handleResetState);
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange(event) {
    const newCurrencyCode = event?.detail?.code;
    if (newCurrencyCode) {
      this.setState({ currentCurrencyCodeAndSymbol: event.detail });
    } else console.error('Currency data not found for:', newCurrencyCode);
    const newCurrencyPriceSetting = findCurrencyItemFromListWithFallback(
      this.props.data.pricePerCurrency,
      newCurrencyCode
    );

    if (newCurrencyPriceSetting)
      this.setState({
        currencyPriceSetting: newCurrencyPriceSetting,
        totalPrice: newCurrencyPriceSetting.totalPrice,
        depositPrice: newCurrencyPriceSetting.depositPrice,
      });
    else console.error('Currency not found from current currency chosen to currency from model');
  }

  handleResetState() {
    this.setState({ isLoading: false });
  }

  getAccomOptions = (accomType) =>
    accomType
      ? this.props.data.accommodationOptions?.filter((ao) => ao.accommodationType === accomType)
      : this.props.data.accommodationOptions;

  getSelectedAccoms = (accomType) =>
    accomType
      ? this.props.data.selectedOptions.accommodations.filter(
          (a) => a.accommodationType === accomType
        )
      : this.props.data.selectedOptions.accommodations;

  getRoomType(value) {
    const { data } = this.props;

    let hotelToUseForRooms = null;
    if (this.getSelectedAccoms(accomTypes.HOTEL).length > 0) {
      hotelToUseForRooms = this.getSelectedAccoms(accomTypes.HOTEL)[0];
    } else if (data.selectedOptions.stopovers.length > 0) {
      hotelToUseForRooms = data.selectedOptions.stopovers[0].hotel;
    }
    if (!hotelToUseForRooms) return null;

    let hotelOptionToUseForRooms = this.getAccomOptions(accomTypes.HOTEL)[0];
    if (!this.getSelectedAccoms(accomTypes.HOTEL).length > 0) {
      const matchingStopover = getStopoverOptionWithSelectedStopover(
        data.stopoverOptions,
        data.selectedOptions.stopovers[0]
      );
      hotelOptionToUseForRooms = matchingStopover.option.hotelOption;
    }

    const { roomTypes } = getOption(hotelOptionToUseForRooms, hotelToUseForRooms.optionKey);
    const selectedRoom = roomTypes.find((room) => room.key === value.key);
    return `${selectedRoom.roomName} ${value.index + 1}`;
  }

  handleEmailCustomerDetailsChange(event) {
    const newState = updateField(this.state, event.target.name, event.target.value);

    this.setState(newState);
  }

  handleDiscountChange(event) {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  }

  handleTermsConditionsChange(event) {
    const { checked, name } = event.target;
    this.setState({ [name]: checked });
  }

  handlePaymentOptionsChange(paymentOption) {
    this.setState({ paymentOption });
  }

  handleEmailSubmit(event) {
    event.preventDefault();

    const { cookieDomain, orderId, labels } = this.props;
    const { sendToCustomer } = this.state;

    const formIsInvalid = validateFieldset(sendToCustomer);

    if (formIsInvalid) {
      return this.setState({
        errorMessage: labels.validationReviewFields,
        isLoading: false,
      });
    }

    if (!REGEX.email.test(sendToCustomer.email.value)) {
      return this.setState({
        errorMessage: labels.validationEmailAddressInvalid,
        isLoading: false,
      });
    }

    if (sendToCustomer.email.value !== sendToCustomer.confirmEmail.value) {
      return this.setState({
        errorMessage: labels.validationEmailAddressesDoNotMatch,
        isLoading: false,
      });
    }

    this.setState({
      errorMessage: null,
      isLoading: true,
    });

    const options = {
      emailAddress: sendToCustomer.email.value,
      firstName: sendToCustomer.firstName.value,
      surname: sendToCustomer.surname.value,
      title: sendToCustomer.title.value,
      cookieDomain,
      orderId,
    };

    return jsonPost(this.props.apiURL, options, (response) => {
      if (response.errorCode || response.errors) {
        if (response.errorCode === errorCodes.TourBookingTourHasReachedPaxCapLimit) {
          window.location.href = response.redirectUri;
        }
        const errorMessageKey = getErrorMessageKey(response.errorCode);
        return this.setState({
          errorMessage:
            this.props.labels[errorMessageKey] || response.message || 'An error occurred',
          isLoading: false,
        });
      }

      resetAuthCookie(cookieDomain, response.token);

      window.location.href = response.redirectUri;
      return this.setState({ errorMessage: null });
    });
  }

  handleSubmit(event) {
    event.preventDefault();

    if (!this.state.termsConditionsAccepted) {
      return this.setState({ errorMessage: this.props.labels.validationAcceptTermsAndConditions });
    }

    this.setState({
      errorMessage: null,
      isLoading: true,
    });

    const options = {
      discountCode: this.state.discountCode,
      marketingSignUp1: this.state.marketingSignUp1,
      marketingSignUp2: this.state.marketingSignUp2,
      marketingSignUp3: this.state.marketingSignUp3,
      marketingSignUp4: this.state.marketingSignUp4,
      paymentOption: Number(this.state.paymentOption),
      priorityAccessPass: this.state.priorityAccessPass,
      termsConditions: this.state.termsConditionsAccepted,
      OrderId: this.props.orderId,
      currency: this.state.currentCurrencyCodeAndSymbol.code,
    };

    return jsonPost(this.props.apiURL, options, (response) => {
      if (response.errorCode || response.errors) {
        if (response.errorCode === errorCodes.TourBookingTourHasReachedPaxCapLimit) {
          window.location.href = response.redirectUri;
        }
        const errorMessageKey = getErrorMessageKey(response.errorCode);
        return this.setState({
          errorMessage:
            this.props.labels[errorMessageKey] || response.message || 'An error occurred',
          isLoading: false,
        });
      }

      window.location.href = response.redirectUri;
      return this.setState({ errorMessage: null });
    });
  }

  render() {
    const { data, labels } = this.props;
    const { isLoading, sendToCustomer } = this.state;

    let selectedOptions = Object.values(data.selectedOptions);
    selectedOptions = selectedOptions.some((item) => item.length > 0);

    const flightSets = [];
    const hasPreEventStopOvers = data.selectedOptions.stopovers.find((stopover) =>
      isStopoverOfType(stopover, StopoverTypes.PRE_EVENT)
    );
    const hasPostEventStopOvers = data.selectedOptions.stopovers.find((stopover) =>
      isStopoverOfType(stopover, StopoverTypes.POST_EVENT)
    );

    if (!hasPreEventStopOvers) {
      flightSets.push({
        flightOptions: data.flightOptions,
        selectedFlights: data.selectedOptions.flights,
      });
    }

    if (hasPreEventStopOvers || hasPostEventStopOvers) {
      data.selectedOptions.stopovers.forEach((selectedStopover) => {
        const stopoverData = getStopoverDataWithDataKey(
          this.props.data.stopoverOptions,
          selectedStopover.dataKey
        );

        if (stopoverData?.flightOptions) {
          flightSets.push({
            stopoverDataKey: stopoverData.key,
            flightOptions: stopoverData?.flightOptions,
            selectedFlights: selectedStopover.flights,
          });
        }
      });
    }

    const allFlights = flightSets.flatMap((flightSet) => flightSet.selectedFlights);
    const allFlightOptions = flightSets.flatMap((flightSet) => flightSet.flightOptions);

    const showAtolLabel = allFlights.length > 0;

    const { updatedTourFromDate, updatedTourToDate } = getTourDatesWithStopoversConsidered(
      data,
      data.selectedOptions.stopovers
    );

    const { preEventStopovers, postEventStopovers } = segregateStopoversByType(
      data.stopoverOptions
    );
    const {
      preEventStopovers: selectedPreEventStopovers,
      postEventStopovers: selectedPostEventStopovers,
    } = segregateStopoversByType(data.selectedOptions.stopovers);

    let hotelToUseForRooms = null;
    if (this.getSelectedAccoms(accomTypes.HOTEL).length > 0) {
      hotelToUseForRooms = this.getSelectedAccoms(accomTypes.HOTEL)[0];
    } else if (data.selectedOptions.stopovers.length > 0) {
      hotelToUseForRooms = data.selectedOptions.stopovers[0].hotel;
    }

    const preEventStopoverItinerary = buildStopoverItineraryDescriptions(
      data.stopoverOptions,
      data.selectedOptions.stopovers,
      StopoverTypes.PRE_EVENT,
      updatedTourFromDate
    );
    const postEventStopoverItinerary = buildStopoverItineraryDescriptions(
      data.stopoverOptions,
      data.selectedOptions.stopovers,
      StopoverTypes.POST_EVENT,
      updatedTourToDate
    );

    return (
      <section className="Booking">
        <form noValidate autoComplete="false" onSubmit={this.handleSubmit}>
          {/* Package summary */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <StickyComponent contained>
                <PackageSummary
                  atolProtectedText={labels.atolProtectedText}
                  currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                  durationText={labels.durationText}
                  fromDate={updatedTourFromDate}
                  toDate={updatedTourToDate}
                  description={data.tourDescription}
                  preferredCardImage={labels.preferredCardImage}
                  price={this.state.totalPrice}
                  showAtolLabel={showAtolLabel}
                  title={data.tourName}
                  totalPriceText={labels.totalPriceText}
                />
              </StickyComponent>
            </div>
          </div>
          {/* Itinerary */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <h2 className="Booking_groupTitle">{labels.packageInformationFieldsetTitle}</h2>
              {preEventStopoverItinerary && (
                <div className="Booking_item">
                  <ItineraryInline
                    dayText={labels.itineraryDayText}
                    itinerary={preEventStopoverItinerary.itinerary}
                    title={preEventStopoverItinerary.itineraryDescription}
                    maskMotorhomesAsCamping={preEventStopoverItinerary.maskMotorhomesAsCamping}
                    campingIcon={preEventStopoverItinerary.campingIcon}
                  />
                </div>
              )}
              <div className="Booking_item">
                <ItineraryInline
                  dayText={labels.itineraryDayText}
                  itinerary={data.itinerarySideBar.itinerary}
                  title={data.tourName}
                  maskMotorhomesAsCamping={data.itinerarySideBar.maskMotorhomesAsCamping}
                  campingIcon={data.itinerarySideBar.campingIcon}
                />
              </div>
              {postEventStopoverItinerary && (
                <div className="Booking_item">
                  <ItineraryInline
                    dayText={labels.itineraryDayText}
                    itinerary={postEventStopoverItinerary.itinerary}
                    title={postEventStopoverItinerary.itineraryDescription}
                    maskMotorhomesAsCamping={postEventStopoverItinerary.maskMotorhomesAsCamping}
                    campingIcon={postEventStopoverItinerary.campingIcon}
                  />
                </div>
              )}
            </div>
          </div>
          {/* Booking Upgrades */}
          {selectedOptions && (
            <div className="Booking_group Booking_group-inset">
              <div className="Booking_content">
                <h2 className="Booking_groupTitle">{labels.upgradesHeader}</h2>
                {/* Pre-event Stopover Upgrades */}
                {selectedPreEventStopovers.length > 0 && preEventStopovers.length > 0 && (
                  <div className="Booking_item Booking_item-inset">
                    <div className="Booking_heading">
                      <h3 className="Booking_itemTitle Booking_itemTitle-small">
                        {labels.preEventStopoverUpgradesHeader}
                      </h3>
                    </div>
                    {selectedPreEventStopovers.map((selectedStopover) => {
                      const stopoverOptions = getStopoverGroupWithSelectedStopover(
                        preEventStopovers,
                        selectedStopover
                      );
                      const preEventStopover = getStopoverOptionWithSelectedStopover(
                        preEventStopovers,
                        selectedStopover
                      );
                      const preEventStopoverData = getStopoverDataWithDataKey(
                        preEventStopovers,
                        selectedStopover.dataKey
                      );

                      const preEventPriceInCurrentCurrency = getPricePerPaxTypePerCurrency(
                        preEventStopoverData.pricePerCurrency,
                        this.state.currentCurrencyCodeAndSymbol.code
                      );

                      return (
                        <div
                          className="Upgrade"
                          key={`${stopoverOptions.key}-${preEventStopover.key}`}
                        >
                          {preEventStopover.option.images.length > 0 && (
                            <div className="Upgrade_imageContainer">
                              <Picture
                                className="Upgrade_image"
                                {...preEventStopover.option.images[0]}
                                sizes={[
                                  [0, 1.77], // from 0
                                  [0, 1.77], // from 480
                                  [0, 1.77], // from 768
                                  [100, 1.77], // from 1024
                                  ['1024', 1.77], // from 1440
                                ]}
                              />
                            </div>
                          )}
                          <div className="Upgrade_content">
                            <h4 className="Upgrade_title">{preEventStopover.option.title}</h4>
                            <span
                              className="Upgrade_field"
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: preEventStopover.option.fullDescription,
                              }}
                            />
                            {preEventStopoverData.numberOfNights > 0 && (
                              <span className="Upgrade_field">
                                {formatDateAndTime(
                                  moment(data.tourFromDate).add(
                                    -preEventStopoverData.numberOfNights,
                                    'd'
                                  )
                                )}
                              </span>
                            )}
                            {preEventStopoverData.numberOfNights > 0 && (
                              <span className="Upgrade_field">
                                {formatDateAndTime(moment(data.tourFromDate).add(-1, 'd'))}
                              </span>
                            )}
                            {preEventPriceInCurrentCurrency > 0 && (
                              <span className="Upgrade_field">
                                <strong>{labels.additionalCostText}: </strong>
                                {this.state.currentCurrencyCodeAndSymbol.symbol}
                                {formatPrice(preEventPriceInCurrentCurrency)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Post-event Stopover Upgrades */}
                {selectedPostEventStopovers.length > 0 && postEventStopovers.length > 0 && (
                  <div className="Booking_item Booking_item-inset">
                    <div className="Booking_heading">
                      <h3 className="Booking_itemTitle Booking_itemTitle-small">
                        {labels.postEventStopoverUpgradesHeader}
                      </h3>
                    </div>
                    {selectedPostEventStopovers.map((selectedStopover) => {
                      const stopoverOptions = getStopoverGroupWithSelectedStopover(
                        postEventStopovers,
                        selectedStopover
                      );
                      const postEventStopover = getStopoverOptionWithSelectedStopover(
                        postEventStopovers,
                        selectedStopover
                      );
                      const postEventStopoverData = getStopoverDataWithDataKey(
                        postEventStopovers,
                        selectedStopover.dataKey
                      );

                      const postEventPriceInCurrentCurrency = getPricePerPaxTypePerCurrency(
                        postEventStopoverData.pricePerCurrency,
                        this.state.currentCurrencyCodeAndSymbol.code
                      );

                      return (
                        <div
                          className="Upgrade"
                          key={`${stopoverOptions.key}-${postEventStopover.key}`}
                        >
                          {postEventStopover.option.images.length > 0 && (
                            <div className="Upgrade_imageContainer">
                              <Picture
                                className="Upgrade_image"
                                {...postEventStopover.option.images[0]}
                                sizes={[
                                  [0, 1.77], // from 0
                                  [0, 1.77], // from 480
                                  [0, 1.77], // from 768
                                  [100, 1.77], // from 1024
                                  ['1024', 1.77], // from 1440
                                ]}
                              />
                            </div>
                          )}
                          <div className="Upgrade_content">
                            <h4 className="Upgrade_title">{postEventStopover.option.title}</h4>
                            <span
                              className="Upgrade_field"
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: postEventStopover.option.fullDescription,
                              }}
                            />
                            {postEventStopoverData.numberOfNights > 0 && (
                              <span className="Upgrade_field">
                                {formatDateAndTime(moment(data.tourToDate).add(1, 'd'))}
                              </span>
                            )}
                            {postEventStopoverData.numberOfNights > 0 && (
                              <span className="Upgrade_field">
                                {formatDateAndTime(
                                  moment(data.tourToDate).add(
                                    postEventStopoverData.numberOfNights,
                                    'd'
                                  )
                                )}
                              </span>
                            )}
                            {postEventPriceInCurrentCurrency > 0 && (
                              <span className="Upgrade_field">
                                <strong>{labels.additionalCostText}: </strong>
                                {this.state.currentCurrencyCodeAndSymbol.symbol}
                                {formatPrice(postEventPriceInCurrentCurrency)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Motorhome Upgrades */}
                {this.getSelectedAccoms(accomTypes.MOTORHOME).length > 0 &&
                  data.accommodationOptions &&
                  this.getAccomOptions(accomTypes.MOTORHOME).length > 0 && (
                    <div className="Booking_item Booking_item-inset">
                      <div className="Booking_heading">
                        <h3 className="Booking_itemTitle Booking_itemTitle-small">
                          {this.state.maskMotorhomesAsCamping
                            ? labels?.campingHeadingText
                            : labels?.motorhomeUpgradesHeader}
                        </h3>
                      </div>
                      {this.getSelectedAccoms(accomTypes.MOTORHOME).map((selectedMotorhome) => {
                        const motorhomeOptions = this.getAccomOptions(accomTypes.MOTORHOME).find(
                          (motorhomeOption) => motorhomeOption.key === selectedMotorhome.groupKey
                        );
                        const motorhome = getOption(motorhomeOptions, selectedMotorhome.optionKey);
                        const motorhomePriceInCurrentCurrency = getPricePerPaxTypePerCurrency(
                          motorhome.totalPricePerCurrency,
                          this.state.currentCurrencyCodeAndSymbol.code
                        );

                        return (
                          <div className="Upgrade" key={`${motorhomeOptions.key}-${motorhome.key}`}>
                            {motorhome.option.images.length > 0 && (
                              <div className="Upgrade_imageContainer">
                                <Picture
                                  className="Upgrade_image"
                                  {...motorhome.option.images[0]}
                                  sizes={[
                                    [0, 1.77], // from 0
                                    [0, 1.77], // from 480
                                    [0, 1.77], // from 768
                                    [100, 1.77], // from 1024
                                    ['1024', 1.77], // from 1440
                                  ]}
                                />
                              </div>
                            )}
                            <div className="Upgrade_content">
                              <h4 className="Upgrade_title">{motorhome.option.title}</h4>
                              <span
                                className="Upgrade_field"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                  __html: motorhome.option.fullDescription,
                                }}
                              />
                              {motorhome.option.numberOfBerth > 0 && (
                                <span className="Upgrade_field">
                                  <strong>
                                    {this.state.maskMotorhomesAsCamping
                                      ? labels?.campingNumberOfBerthText
                                      : labels?.numberOfBerthText}
                                    :{' '}
                                  </strong>{' '}
                                  {motorhome.option.numberOfBerth}
                                </span>
                              )}
                              {motorhomePriceInCurrentCurrency > 0 && (
                                <span className="Upgrade_field">
                                  <strong>{labels.additionalCostText}: </strong>
                                  {this.state.currentCurrencyCodeAndSymbol.symbol}

                                  {formatPrice(motorhomePriceInCurrentCurrency)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                {/* Hotel Upgrades */}
                {this.getSelectedAccoms(accomTypes.HOTEL).length > 0 &&
                  data.accommodationOptions &&
                  this.getAccomOptions(accomTypes.HOTEL).length > 0 && (
                    <div className="Booking_item Booking_item-inset">
                      <div className="Booking_heading">
                        <h3 className="Booking_itemTitle Booking_itemTitle-small">
                          {labels.hotelUpgradesHeader}
                        </h3>
                      </div>
                      {this.getSelectedAccoms(accomTypes.HOTEL).map((selectedHotel) => {
                        const hotelOptions = this.getAccomOptions(accomTypes.HOTEL).find(
                          (hotelOption) => hotelOption.key === selectedHotel.groupKey
                        );
                        const hotel = getOption(hotelOptions, selectedHotel.optionKey);

                        return (
                          <div className="Upgrade" key={`${hotelOptions.key}-${hotel.key}`}>
                            {hotel.option.images.length > 0 && (
                              <div className="Upgrade_imageContainer">
                                <Picture
                                  className="Upgrade_image"
                                  {...hotel.option.images[0]}
                                  sizes={[
                                    [0, 1.77], // from 0
                                    [0, 1.77], // from 480
                                    [0, 1.77], // from 768
                                    [100, 1.77], // from 1024
                                    ['1024', 1.77], // from 1440
                                  ]}
                                />
                              </div>
                            )}
                            <div className="Upgrade_content">
                              <h4 className="Upgrade_title">{hotel.option.title}</h4>
                              <span
                                className="Upgrade_field"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                  __html: hotel.option.fullDescription,
                                }}
                              />
                              {hotel.checkInDate && (
                                <span className="Upgrade_field">
                                  {formatDateAndTime(hotel.checkInDate, hotel.option.checkInTime)}
                                </span>
                              )}
                              {hotel.checkOutDate && (
                                <span className="Upgrade_field">
                                  {formatDateAndTime(hotel.checkOutDate, hotel.option.checkOutTime)}
                                </span>
                              )}
                              <span className="Upgrade_field">
                                <strong>{this.props.labels.hotelRoomsText ?? 'Rooms'}</strong>
                              </span>
                              {selectedHotel.rooms
                                .filter((room) => room.roomCount > 0)
                                .map((room) => (
                                  <span
                                    className="Upgrade_field"
                                    key={`${selectedHotel.key}-${room.key}`}
                                  >{`${
                                    hotel.roomTypes.find(
                                      (roomType) => roomType.key === room.roomKey
                                    ).roomName
                                  } x${room.roomCount}`}</span>
                                ))}
                              {hotel.price > 0 && (
                                <span className="Upgrade_field">
                                  <strong>{labels.additionalCostText}: </strong>
                                  {this.state.currentCurrencyCodeAndSymbol.symbol}
                                  {formatPrice(hotel.price)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                {/* Flight Upgrades */}
                {allFlights.length > 0 && allFlightOptions && allFlightOptions.length > 0 && (
                  <div className="Booking_item Booking_item-inset">
                    <div className="Booking_heading">
                      <h3 className="Booking_itemTitle Booking_itemTitle-small">
                        {labels.flightUpgradesHeader}
                      </h3>
                    </div>

                    {allFlights.map((selectedFlight) => {
                      const flightOptions = allFlightOptions.find(
                        (flightOption) => flightOption.key === selectedFlight.groupKey
                      );
                      const flight = getOption(flightOptions, selectedFlight.optionKey);
                      const selectedFlightClass = flight.flightClasses.find(
                        (flightClass) => flightClass.key === selectedFlight.flightClassKey
                      );

                      return (
                        <div className="Upgrade" key={`${flightOptions.key}-${flight.key}`}>
                          <div className="Upgrade_content">
                            <h4 className="Upgrade_title">{flight.option.title}</h4>
                            {flight.option.fullDescription.charAt(0) === '<' ? (
                              <span
                                className="Upgrade_field"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                  __html: flight.option.fullDescription,
                                }}
                              />
                            ) : (
                              <span className="Upgrade_field">{flight.option.fullDescription}</span>
                            )}
                            {flight.option.outboundDepartureAirport && (
                              <span className="Upgrade_field">
                                <strong>{labels.flightDepartureLocationText}: </strong>
                                {flight.option.outboundDepartureAirport}
                              </span>
                            )}
                            {flight.option.outboundArrivalAirport && (
                              <span className="Upgrade_field">
                                <strong>{labels.flightArrivalLocationText}: </strong>
                                {flight.option.outboundArrivalAirport}
                              </span>
                            )}
                            {(flight.option.outboundDepartureDate ||
                              flight.option.outboundDepartureTime) && (
                              <span className="Upgrade_field">
                                <strong>{labels.flightDepartureDateTimeText}: </strong>
                                {formatDateAndTime(
                                  flight.option.outboundDepartureDate,
                                  flight.option.outboundDepartureTime
                                )}
                              </span>
                            )}
                            {(flight.option.outboundArrivalDate ||
                              flight.option.outboundArrivalTime) && (
                              <span className="Upgrade_field">
                                <strong>{labels.flightArrivalDateTimeText}: </strong>
                                {formatDateAndTime(
                                  flight.option.outboundArrivalDate,
                                  flight.option.outboundArrivalTime
                                )}
                              </span>
                            )}
                            {flight.option.outboundFlightNumber && (
                              <span className="Upgrade_field">
                                <strong>{labels.flightNumberText}: </strong>
                                {flight.option.outboundFlightNumber}
                              </span>
                            )}
                            <span className="Upgrade_field">
                              <strong>{labels.flightClassText}: </strong>
                              {selectedFlightClass.option.title}
                            </span>
                            {selectedFlightClass.price > 0 && (
                              <span className="Upgrade_field">
                                <strong>{labels.additionalCostText}: </strong>
                                {this.state.currentCurrencyCodeAndSymbol.symbol}
                                {formatPrice(selectedFlightClass.price)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Tickets Upgrades */}
                {data.selectedOptions.tickets.length > 0 &&
                  data.ticketOptions &&
                  data.ticketOptions.length > 0 && (
                    <div className="Booking_item Booking_item-inset">
                      <div className="Booking_heading">
                        <h3 className="Booking_itemTitle Booking_itemTitle-small">
                          {labels.ticketUpgradesHeader}
                        </h3>
                      </div>
                      {data.selectedOptions.tickets.map((selectedTicket) => {
                        const ticketOptions = data.ticketOptions.find(
                          (ticketOption) => ticketOption.key === selectedTicket.groupKey
                        );
                        const ticket = getOption(ticketOptions, selectedTicket.optionKey);
                        const passengers = getPassengers(data.paxSelection, data.paxTypes);

                        return (
                          <div className="Upgrade" key={`${ticketOptions.key}-${ticket.key}`}>
                            <div className="Upgrade_content">
                              <h4 className="Upgrade_title">{ticket.option.title}</h4>
                              {ticketOptions.matchKickOffDate && (
                                <span className="Upgrade_field">
                                  {formatDateAndTime(
                                    ticketOptions.matchKickOffDate,
                                    ticketOptions.matchKickOffTime
                                  )}
                                </span>
                              )}
                              {ticket.option.fullDescription &&
                              ticket.option.fullDescription.charAt(0) === '<' ? (
                                <span
                                  className="Upgrade_field"
                                  // eslint-disable-next-line react/no-danger
                                  dangerouslySetInnerHTML={{
                                    __html: ticket.option.fullDescription,
                                  }}
                                />
                              ) : (
                                <span className="Upgrade_field">{ticket.option.fullDetails}</span>
                              )}
                              {ticket.option.category && (
                                <span className="Upgrade_field">
                                  <strong>{labels.ticketCategoryText}: </strong>
                                  {ticket.option.category}
                                </span>
                              )}
                              {ticket.option.category && (
                                <span className="Upgrade_field">
                                  <strong>{labels.paxSummaryText}: </strong>
                                  {passengers}
                                </span>
                              )}
                              {ticket.price > 0 && (
                                <span className="Upgrade_field">
                                  <strong>{labels.additionalCostText}: </strong>
                                  {this.state.currentCurrencyCodeAndSymbol.symbol}
                                  {formatPrice(ticket.price)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                {/* Transport Upgrades */}
                {data.selectedOptions.transports.length > 0 &&
                  data.transportOptions &&
                  data.transportOptions.length > 0 && (
                    <div className="Booking_item Booking_item-inset">
                      <div className="Booking_heading">
                        <h3 className="Booking_itemTitle Booking_itemTitle-small">
                          {labels.transportUpgradesHeader}
                        </h3>
                      </div>
                      {data.selectedOptions.transports.map((selectedTransport) => {
                        const transportOptions = data.transportOptions.find(
                          (transportOption) => transportOption.key === selectedTransport.groupKey
                        );
                        const transportGroup = [
                          transportOptions.defaultOption,
                          ...transportOptions.upgrades,
                        ].find((outerOption) =>
                          outerOption.options.find(
                            (innerOption) => innerOption.key === selectedTransport.optionKey
                          )
                        );
                        const transport = transportGroup.options.find(
                          (option) => option.key === selectedTransport.optionKey
                        );

                        return (
                          <div className="Upgrade" key={`${transportGroup.key}-${transport.key}`}>
                            <div className="Upgrade_content">
                              <h4 className="Upgrade_title">{transportGroup.cardTitle}</h4>
                              {transport.fullDescription &&
                              transport.fullDescription.charAt(0) === '<' ? (
                                <span
                                  className="Upgrade_field"
                                  // eslint-disable-next-line react/no-danger
                                  dangerouslySetInnerHTML={{
                                    __html: transport.fullDescription,
                                  }}
                                />
                              ) : (
                                <span className="Upgrade_field">{transport.fullDescription}</span>
                              )}
                              {transport.option.outboundDepartureLocation && (
                                <>
                                  <span className="Upgrade_field">
                                    <strong>{labels.transportDepartureLocationText}: </strong>
                                    {transport.option.outboundDepartureLocation}
                                  </span>
                                  <span className="Upgrade_field">
                                    <strong>{labels.transportArrivalLocationText}: </strong>
                                    {transport.option.outboundArrivalLocation}
                                  </span>
                                </>
                              )}
                              {transport.option.outboundDepartureDate && (
                                <span className="Upgrade_field">
                                  <strong>{labels.transportOutboundDepartureDateTimeText}: </strong>
                                  {formatDateAndTime(
                                    transport.option.outboundDepartureDate,
                                    transport.option.outboundDepartureTime
                                  )}
                                </span>
                              )}
                              {transport.option.outboundArrivalDate && (
                                <span className="Upgrade_field">
                                  <strong>{labels.transportOutboundArrivalDateTimeText}: </strong>
                                  {formatDateAndTime(
                                    transport.option.outboundArrivalDate,
                                    transport.option.outboundArrivalDate
                                  )}
                                </span>
                              )}
                              {transport.option.inboundDepartureDate && (
                                <span className="Upgrade_field">
                                  <strong>{labels.transportInboundDepartureDateTimeText}: </strong>
                                  {formatDateAndTime(
                                    transport.option.inboundDepartureDate,
                                    transport.option.inboundDepartureTime
                                  )}
                                </span>
                              )}
                              {transport.option.inboundArrivalDate && (
                                <span className="Upgrade_field">
                                  <strong>{labels.transportInboundArrivalDateTimeText}: </strong>
                                  {formatDateAndTime(
                                    transport.option.inboundArrivalDate,
                                    transport.option.inboundArrivalTime
                                  )}
                                </span>
                              )}
                              {transport.price > 0 && (
                                <span className="Upgrade_field">
                                  <strong>{labels.additionalCostText}: </strong>
                                  {this.state.currentCurrencyCodeAndSymbol.symbol}
                                  {formatPrice(transport.price)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                {/* Extras Upgrades */}
                {data.selectedOptions.excursions.length > 0 &&
                  data.excursionOptions &&
                  data.excursionOptions.length > 0 && (
                    <div className="Booking_item Booking_item-inset">
                      <div className="Booking_heading">
                        <h3 className="Booking_itemTitle Booking_itemTitle-small">
                          {labels.selectedExcursionsHeader}
                        </h3>
                      </div>
                      {data.selectedOptions.excursions.map((selectedExcursion) => {
                        const excursionOptions = data.excursionOptions.find(
                          (excursionOption) => excursionOption.key === selectedExcursion.groupKey
                        );
                        const excursion = excursionOptions.options.find(
                          (option) => option.key === selectedExcursion.optionKey
                        );

                        return (
                          <div className="Upgrade" key={`${excursionOptions.key}-${excursion.key}`}>
                            <div className="Upgrade_content">
                              <h4 className="Upgrade_title">{excursion.title}</h4>
                              {excursion.fullDescription.charAt(0) === '<' ? (
                                <span
                                  className="Upgrade_field"
                                  // eslint-disable-next-line react/no-danger
                                  dangerouslySetInnerHTML={{
                                    __html: excursion.fullDescription,
                                  }}
                                />
                              ) : (
                                <span className="Upgrade_field">{excursion.fullDescription}</span>
                              )}
                              {excursion.date && (
                                <div className="UpgradeCard_dateTime">
                                  <span className="UpgradeCard_dateTimeLabel">
                                    {labels.excursionsDateText}:{' '}
                                  </span>
                                  {formatDateAndTime(excursion.date)}
                                </div>
                              )}

                              {excursion.time && (
                                <div className="UpgradeCard_dateTime">
                                  <span className="UpgradeCard_dateTimeLabel">
                                    {labels.excursionsTimeText}:{' '}
                                  </span>
                                  {formatDateAndTime(null, excursion.time)}
                                </div>
                              )}

                              {excursion.price > 0 && (
                                <span className="Upgrade_field">
                                  <strong>{labels.additionalCostText}: </strong>
                                  {this.state.currentCurrencyCodeAndSymbol.symbol}
                                  {formatPrice(excursion.price)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                {/* Miscellaneous Extras */}
                {data.extras && data.extras.length > 0 && (
                  <div className="Booking_item Booking_item-inset">
                    <div className="Booking_heading">
                      <h3 className="Booking_itemTitle Booking_itemTitle-small">
                        {labels.extrasTitle}
                      </h3>
                    </div>
                    {data.extras.map((extra) => (
                      <div className="Upgrade" key={extra.key}>
                        <div className="Upgrade_content">
                          <h4 className="Upgrade_title">{extra.title}</h4>
                          {extra.fullDescription.charAt(0) === '<' ? (
                            <span
                              className="Upgrade_field"
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{ __html: extra.fullDescription }}
                            />
                          ) : (
                            <span className="Upgrade_field">{extra.fullDescription}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Lead party member */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <h2 className="Booking_groupTitle">{labels.partyDetailsFieldsetHeaderText}</h2>
              <fieldset className="Booking_item">
                <div className="Booking_heading">
                  <h3 className="Booking_itemTitle">{labels.leadPartyMemberFieldsetTitle}</h3>
                  <h4 className="Booking_itemSubTitle">{labels.partyMemberFieldsetSubtitle}</h4>
                </div>
                <MemberDetails
                  fieldNamePrefix="leadPartyMember"
                  fieldset={data.leadBooker}
                  isDisabled
                  titleOptions={labels.titleOptions}
                />
              </fieldset>
              <fieldset className="Booking_item">
                <div className="Booking_heading">
                  <h4 className="Booking_itemTitle Booking_itemTitle-small">
                    {labels.addressFieldsetTitle}
                  </h4>
                </div>
                <MemberAddress
                  fieldNamePrefix="leadPartyMember"
                  fieldset={data.leadBooker}
                  isDisabled
                  addressLine2PlaceholderText={labels.placeholderTexts.addressLine2PlaceholderText}
                  telephonePlaceholderText={labels.placeholderTexts.telephonePlaceholderText}
                />
              </fieldset>
            </div>
          </div>
          {/* Party members */}
          {data.partyMembers.map((partyMember, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="Booking_group Booking_group-inset" key={index}>
              <div className="Booking_content">
                <fieldset className="Booking_item">
                  <div className="Booking_heading">
                    <h3 className="Booking_itemTitle">
                      {labels.partyMemberFieldsetTitle} {index + 1}
                    </h3>
                  </div>
                  <MemberDetails
                    fieldNamePrefix={`partyMembers[${index}]`}
                    fieldset={partyMember}
                    isDisabled
                    chestSizeOptions={data.chestSizeOptions}
                    titleOptions={labels.titleOptions}
                    showExtras
                    specialRequirementsPlaceholderText={labels.specialRequirementsPlaceholderText}
                  />
                </fieldset>
                {hotelToUseForRooms?.rooms.length > 0 && (
                  <fieldset className="Booking_item">
                    <div className="Booking_heading">
                      <h4 className="Booking_itemTitle Booking_itemTitle-small">
                        {labels.roomAllocationFieldsetTitle}
                      </h4>
                    </div>
                    <div className="Booking_roomType">
                      {this.getRoomType(partyMember.roomAllocation)}
                    </div>
                  </fieldset>
                )}
              </div>
            </div>
          ))}
          {/* Is staff member logged in */}
          {data.isSalesAgentBooking && !data.isSalesAgentUserLoggedIn && (
            <div className="Booking_group Booking_group-inset">
              <div className="Booking_content">
                <fieldset className="Booking_item">
                  <div className="Booking_heading">
                    <h3 className="Booking_itemTitle">{labels.salesAgentSendToUserHeadingText}</h3>
                  </div>
                  <EmailCustomerDetails
                    confirmEmailAddressPlaceholderText={
                      labels.placeholderTexts.confirmEmailAddressPlaceholderText
                    }
                    emailAddressPlaceholderText={
                      labels.placeholderTexts.emailAddressPlaceholderText
                    }
                    fieldNamePrefix="sendToCustomer"
                    fieldset={sendToCustomer}
                    forenamePlaceholderText={labels.placeholderTexts.forenamePlaceholderText}
                    handleFieldChange={this.handleEmailCustomerDetailsChange}
                    surnamePlaceholderText={labels.placeholderTexts.surnamePlaceholderText}
                    titleOptions={labels.titleOptions}
                    titlePlaceholderText={labels.placeholderTexts.titlePlaceholderText}
                  />
                </fieldset>
              </div>
            </div>
          )}
          {/* Is Client logged in */}
          {data.isSalesAgentBooking && data.isSalesAgentUserLoggedIn && (
            <div className="Booking_group Booking_group-inset">
              <div className="Booking_content">
                <ClientEditDetails
                  editButtonText={labels.editPartyDetailsButtonText}
                  editMessage={labels.editPartyDetailsMessage}
                  editURL={`${data.editPartyDetailsUrl}?orderId=${this.props.orderId}`}
                  title={labels.editPartyDetailsTitle}
                />
              </div>
            </div>
          )}
          {/* Linked Bookings */}
          {data.linkedBookingReference && (
            <div className="Booking_group Booking_group-inset">
              <div className="Booking_content">
                <LinkedBookings
                  isDisabled
                  linkedBookingHeadingText={labels.linkedBookingHeadingText}
                  linkedBookingLabelText={labels.linkedBookingLabelText}
                  linkedBookingName={data.linkedBookingName}
                  linkedBookingNameLabelText={labels.linkedBookingNameLabelText}
                  linkedBookingReference={data.linkedBookingReference}
                  linkedBookingReferenceLabelText={labels.linkedBookingReferenceLabelText}
                />
              </div>
            </div>
          )}
          {/* Insurance */}
          {(!data.isSalesAgentBooking || data.isSalesAgentUserLoggedIn) &&
            data.insuranceText &&
            data.insuranceText !== '' && (
              <div className="Booking_group Booking_group-inset">
                <div className="Booking_content">
                  <Insurance
                    isDisabled
                    notRequiredOptionText={labels.insuranceNotRequiredOptionText}
                    partyMembers={data.partyMembers}
                    requiredOptionText={labels.insuranceRequiredOptionText}
                    text={data.insuranceText}
                    title={labels.insuranceFieldsetTitle}
                  />
                </div>
              </div>
            )}
          {/* Discounts */}
          {(!data.isSalesAgentBooking || data.isSalesAgentUserLoggedIn) &&
            !labels.removeDiscountsSection && (
              <div className="Booking_group">
                <div className="Booking_content Booking_content-inset">
                  <Discount
                    discountLabelText={labels.discountLabelText}
                    discountCodeValue={this.state.discountCode}
                    handleChange={this.handleDiscountChange}
                    hideDiscountInput={labels.hideDiscountInput}
                    hidePriorityAccessPassInput={labels.hidePriorityAccessPassInput}
                    priorityAccessPassLabelText={labels.priorityAccessPassLabelText}
                    priorityAccessPassValue={this.state.priorityAccessPass}
                    text={labels.discountText}
                    title={labels.discountFieldsetTitle}
                  />
                </div>
              </div>
            )}
          {/* Terms and conditions */}
          {(!data.isSalesAgentBooking || data.isSalesAgentUserLoggedIn) && (
            <div className="Booking_group Booking_group-inset">
              <div className="Booking_content">
                <TermsConditions
                  handleCheckboxChange={this.handleTermsConditionsChange}
                  signUpAccepted1={this.state.marketingSignUp1}
                  signUpAccepted2={this.state.marketingSignUp2}
                  signUpAccepted3={this.state.marketingSignUp3}
                  signUpAccepted4={this.state.marketingSignUp4}
                  signUpLabelText1={labels.marketingSignUp1LabelText}
                  signUpLabelText2={labels.marketingSignUp2LabelText}
                  signUpLabelText3={labels.marketingSignUp3LabelText}
                  signUpLabelText4={labels.marketingSignUp4LabelText}
                  title={labels.termsConditionsHeaderText}
                  termsConditionsAccepted={this.state.termsConditionsAccepted}
                  termsConditionsLabelText={labels.termsConditionsLabelText}
                  termsConditionsText={data.termsAndConditionsText}
                />
              </div>
            </div>
          )}
          {/* Payment options */}
          {(!data.isSalesAgentBooking || data.isSalesAgentUserLoggedIn) && (
            <div className="Booking_group Booking_group-inset">
              <div className="Booking_content">
                <h2 className="Booking_groupTitle">{labels.paymentFieldsetTitle}</h2>
                <fieldset className="Booking_item">
                  <PaymentOptions
                    currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                    handleChange={this.handlePaymentOptionsChange}
                    hasDepositOption={data.allowsDeposit}
                    hasBankPaymentOption={data.bankTransferPaymentsEnabled}
                    preferredCardImage={labels.preferredCardImage}
                    price={this.state.totalPrice}
                    priceDeposit={this.state.depositPrice}
                    priceText={labels.totalToPayText}
                    optionBankOptionText={labels.paymentOptionBankTransferText}
                    optionBankDepositText={labels.paymentOptionBankTransferPayDepositText}
                    optionBankFullText={labels.paymentOptionBankTransferPayInFullText}
                    optionCardOptionText={labels.paymentOptionCardText}
                    optionCardDepositText={labels.paymentOptionCardPayDepositText}
                    optionCardFullText={labels.paymentOptionCardPayInFullText}
                    selectedValue={this.state.paymentOption}
                  />
                </fieldset>
              </div>
            </div>
          )}
          {/* Submit */}
          <div className="Booking_group" ref={this.submitContainerRef}>
            {this.state.errorMessage && (
              <div className="Booking_item">
                <div className="Booking_group">
                  <ErrorMessage text={this.state.errorMessage} />
                </div>
              </div>
            )}
            <div className="Booking_item Booking_item-center">
              {data.isSalesAgentBooking && !data.isSalesAgentUserLoggedIn && (
                <Button
                  buttonType="submit"
                  handleClick={this.handleEmailSubmit}
                  isLoading={isLoading}
                  modifier="fill"
                  text={labels.salesAgentSendCustomerEmailButtonText}
                />
              )}
              {(!data.isSalesAgentBooking || data.isSalesAgentUserLoggedIn) && (
                <Button
                  buttonType="submit"
                  handleClick={this.handleSubmit}
                  isLoading={isLoading}
                  modifier="fill"
                  text={labels.submitButtonText}
                />
              )}
            </div>
          </div>
        </form>
      </section>
    );
  }
}

BookingSummary.propTypes = propTypes;
BookingSummary.defaultProps = defaultProps;

export default withErrorBoundary(BookingSummary);
